/**
 * Copyright (c) 2026 Stefan Radig
 *
 * Admin panel: access tokens, invite codes, plugin marketplace, settings.
 * Buttons carry data-action attributes handled by one delegated click listener
 * (the page must not contain inline event handlers under the Content-Security-Policy).
 */

let adminToken = '';
const API_BASE = window.location.origin + window.location.pathname.replace(/\/[^/]*\/[^/]*\/[^/]*$/, '');

interface TokenRecord {
    id: number;
    token_name: string;
    token_preview: string;
    created_at: string;
    expires_at: string | null;
    last_used_at: string | null;
    revoked_at: string | null;
    usage_count: number;
}

interface InviteRecord {
    id: number;
    code: string;
    notes: string | null;
    created_at: string;
    expires_at: string | null;
    used_at: string | null;
    max_uses: number | string | null;
    use_count: number | string | null;
    target_dir: string | null;
}

interface RegistryPlugin {
    id: string;
    name: string;
    description?: string;
    icon?: string;
    version: string;
    installed: boolean;
    installedVersion: string | null;
    manuallyInstalled?: boolean;
    enabled?: boolean;
}

function el<T extends HTMLElement = HTMLElement>(id: string): T {
    const element = document.getElementById(id);
    if (!element) throw new Error(`Admin page is missing #${id}`);
    return element as T;
}

function escapeHtml(value: unknown): string {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function adminHeaders(json = false): Record<string, string> {
    const headers: Record<string, string> = { 'X-Admin-Token': adminToken };
    if (json) headers['Content-Type'] = 'application/json';
    return headers;
}

// Tab switching
function switchTab(tabId: string): void {
    document.querySelectorAll<HTMLElement>('.tab').forEach(tab => {
        tab.classList.toggle('active', tab.dataset.tab === tabId);
    });
    document.querySelectorAll<HTMLElement>('.tab-content').forEach(content => {
        content.classList.toggle('active', content.id === `tab-${tabId}`);
    });
    localStorage.setItem('adminTab', tabId);
}

// Toast notification system
type ToastType = 'success' | 'error' | 'warning' | 'info';

class Toast {
    private static container: HTMLDivElement | null = null;

    private static ensureContainer(): HTMLDivElement {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.style.cssText = `
                position: fixed;
                top: 20px;
                right: 20px;
                z-index: 10000;
                display: flex;
                flex-direction: column;
                gap: 10px;
                pointer-events: none;
            `;
            document.body.appendChild(this.container);
        }
        return this.container;
    }

    static show(message: string, type: ToastType = 'info', duration = 3000): void {
        const container = this.ensureContainer();
        const toast = document.createElement('div');

        const colors: Record<ToastType, string> = {
            success: '#2ecc71',
            error: '#e74c3c',
            warning: '#f39c12',
            info: '#3498db',
        };
        const icons: Record<ToastType, string> = {
            success: '✓',
            error: '✗',
            warning: '⚠',
            info: 'ℹ',
        };

        toast.style.cssText = `
            background: ${colors[type]};
            color: white;
            padding: 12px 20px;
            border-radius: 6px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            font-size: 14px;
            max-width: 350px;
            word-wrap: break-word;
            pointer-events: auto;
            cursor: pointer;
            transition: opacity 0.3s, transform 0.3s;
            transform: translateX(400px);
            opacity: 0;
        `;

        const icon = document.createElement('span');
        icon.style.marginRight = '8px';
        icon.textContent = icons[type];
        toast.append(icon, document.createTextNode(message));
        container.appendChild(toast);

        requestAnimationFrame(() => {
            toast.style.transform = 'translateX(0)';
            toast.style.opacity = '1';
        });

        toast.addEventListener('click', () => this.dismiss(toast));
        setTimeout(() => this.dismiss(toast), duration);
    }

    private static dismiss(toast: HTMLDivElement): void {
        toast.style.transform = 'translateX(400px)';
        toast.style.opacity = '0';
        setTimeout(() => {
            toast.parentElement?.removeChild(toast);
        }, 300);
    }
}

let setupMode = false;

// Switch the login form to first-time setup (server returned 428 requires_setup)
function enterSetupMode(): void {
    setupMode = true;
    el('loginTitle').textContent = 'Admin Setup';
    el('setupHint').classList.remove('hidden');
    el('setupConfirmGroup').classList.remove('hidden');
    el('loginBtn').textContent = 'Set Admin Token';
}

// Mirrors the server-side check in admin_auth_helper.php
function isStrongAdminToken(token: string): boolean {
    if (token.length < 16) return false;
    if (new Set(token).size < 8) return false;
    if (token.length >= 24) return true;
    let classes = 0;
    if (/[a-z]/.test(token)) classes++;
    if (/[A-Z]/.test(token)) classes++;
    if (/[0-9]/.test(token)) classes++;
    if (/[^a-zA-Z0-9]/.test(token)) classes++;
    return classes >= 3;
}

async function setupAdminToken(token: string): Promise<void> {
    if (!isStrongAdminToken(token)) {
        Toast.show('Token too weak: use 16+ characters mixing 3+ character types, or 24+ varied characters', 'warning');
        return;
    }
    if (token !== el<HTMLInputElement>('adminTokenConfirmInput').value) {
        Toast.show('Tokens do not match', 'warning');
        return;
    }

    try {
        const response = await fetch(`${API_BASE}/php/auth_setup_admin.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ admin_token: token }),
        });
        const data = await response.json();

        if (response.ok && data.success) {
            Toast.show('Admin token configured', 'success');
            setupMode = false;
            await login();
        } else {
            Toast.show(data.error || 'Setup failed', 'error');
        }
    } catch (error) {
        Toast.show('Error: ' + (error as Error).message, 'error');
    }
}

function showAdminPanel(): void {
    el('loginForm').classList.add('hidden');
    el('adminPanel').classList.remove('hidden');
    void loadData();
}

async function login(): Promise<void> {
    const token = el<HTMLInputElement>('adminTokenInput').value;
    if (!token) {
        Toast.show('Please enter admin token', 'warning');
        return;
    }

    if (setupMode) {
        await setupAdminToken(token);
        return;
    }

    adminToken = token;

    try {
        // Test if token works
        const response = await fetch(`${API_BASE}/php/auth_admin.php?action=settings`, { headers: adminHeaders() });

        if (response.ok) {
            localStorage.setItem('editoor_admin_token', adminToken);
            showAdminPanel();
        } else if (response.status === 428) {
            // No admin token configured yet - switch to first-time setup
            adminToken = '';
            enterSetupMode();
        } else {
            Toast.show('Invalid admin token', 'error');
        }
    } catch (error) {
        Toast.show('Error: ' + (error as Error).message, 'error');
    }
}

function logout(): void {
    localStorage.removeItem('editoor_admin_token');
    adminToken = '';
    el('loginForm').classList.remove('hidden');
    el('adminPanel').classList.add('hidden');
    el<HTMLInputElement>('adminTokenInput').value = '';
}

// Check for saved token on page load
async function autoLogin(): Promise<void> {
    const savedToken = localStorage.getItem('editoor_admin_token');
    if (!savedToken) return;
    adminToken = savedToken;
    try {
        const response = await fetch(`${API_BASE}/php/auth_admin.php?action=settings`, { headers: adminHeaders() });
        if (response.ok) {
            showAdminPanel();
        } else {
            // Token invalid, clear it
            localStorage.removeItem('editoor_admin_token');
            adminToken = '';
            if (response.status === 428) {
                enterSetupMode();
            }
        }
    } catch (error) {
        console.error('Auto-login failed:', error);
    }
}

async function loadData(): Promise<void> {
    await loadSettings();
    await loadTokens();
    await loadInvites();
    await loadPluginSettings();
    await refreshRegistry();
}

async function loadSettings(): Promise<void> {
    const response = await fetch(`${API_BASE}/php/auth_admin.php?action=settings`, { headers: adminHeaders() });
    const data = await response.json();
    el<HTMLInputElement>('authRequiredToggle').checked = data.settings.auth_required === 'true';
}

async function loadTokens(): Promise<void> {
    const response = await fetch(`${API_BASE}/php/auth_admin.php?action=tokens`, { headers: adminHeaders() });
    const data = await response.json();
    const tokens = data.tokens as TokenRecord[];

    const activeTokens = tokens.filter(t => !t.revoked_at);
    const revokedTokens = tokens.filter(t => t.revoked_at);

    // Render active tokens
    const tbody = el('tokensTableBody');
    tbody.innerHTML = '';

    activeTokens.forEach(token => {
        const tr = document.createElement('tr');

        // Format expiry
        let expiryText = 'Never';
        let expiryClass = '';
        if (token.expires_at) {
            const expiryDate = new Date(token.expires_at);
            const now = new Date();
            const isExpired = expiryDate < now;
            const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

            expiryText = expiryDate.toLocaleDateString();
            if (isExpired) {
                expiryClass = 'status-revoked';
                expiryText += ' (expired)';
            } else if (daysUntilExpiry <= 7) {
                expiryClass = 'status-warning';
                expiryText += ` (${daysUntilExpiry}d)`;
            }
        }

        tr.innerHTML = `
            <td>${escapeHtml(token.token_name)}</td>
            <td><span class="token-preview">${escapeHtml(token.token_preview)}</span></td>
            <td>${new Date(token.created_at).toLocaleDateString()}</td>
            <td class="${expiryClass}">${expiryText}</td>
            <td>${token.last_used_at ? new Date(token.last_used_at).toLocaleDateString() : 'Never'}</td>
            <td>${token.usage_count}</td>
            <td>
                <button class="btn btn-danger" data-action="revoke-token" data-id="${token.id}">Revoke</button>
            </td>
        `;
        tbody.appendChild(tr);
    });

    // Show/hide empty state
    el('tokensTable').classList.toggle('hidden', activeTokens.length === 0);
    el('noActiveTokens').classList.toggle('hidden', activeTokens.length > 0);

    // Render revoked tokens
    const revokedSection = el('revokedTokensSection');
    const revokedTbody = el('revokedTokensTableBody');
    revokedTbody.innerHTML = '';

    if (revokedTokens.length > 0) {
        revokedSection.classList.remove('hidden');
        el('revokedTokensCount').textContent = String(revokedTokens.length);

        revokedTokens.forEach(token => {
            const tr = document.createElement('tr');
            tr.innerHTML = `
                <td>${escapeHtml(token.token_name)}</td>
                <td><span class="token-preview">${escapeHtml(token.token_preview)}</span></td>
                <td>${new Date(token.created_at).toLocaleDateString()}</td>
                <td>${new Date(token.revoked_at!).toLocaleDateString()}</td>
                <td>${token.usage_count}</td>
            `;
            revokedTbody.appendChild(tr);
        });
    } else {
        revokedSection.classList.add('hidden');
    }
}

async function toggleAuthRequired(): Promise<void> {
    const enabled = el<HTMLInputElement>('authRequiredToggle').checked;

    const response = await fetch(`${API_BASE}/php/auth_admin.php?action=settings`, {
        method: 'PUT',
        headers: adminHeaders(true),
        body: JSON.stringify({ auth_required: enabled ? 'true' : 'false' }),
    });

    if (response.ok) {
        Toast.show(`Authentication ${enabled ? 'enabled' : 'disabled'}`, 'success');
    }
}

async function createToken(): Promise<void> {
    const name = el<HTMLInputElement>('newTokenName').value;
    const expiry = el<HTMLInputElement>('newTokenExpiry').value;

    if (!name) {
        Toast.show('Please enter a token name', 'warning');
        return;
    }

    const response = await fetch(`${API_BASE}/php/auth_admin.php?action=create_token`, {
        method: 'POST',
        headers: adminHeaders(true),
        body: JSON.stringify({
            token_name: name,
            expires_in_days: expiry ? parseInt(expiry) : null,
        }),
    });

    const data = await response.json();

    if (data.success) {
        el('newTokenDisplay').textContent = data.token;
        el('tokenModal').style.display = 'block';
        el<HTMLInputElement>('newTokenName').value = '';
        void loadTokens();
    }
}

async function revokeToken(id: string): Promise<void> {
    const confirmed = await showConfirmModal('This action cannot be undone.', 'Revoke Token?');
    if (!confirmed) return;

    const response = await fetch(`${API_BASE}/php/auth_admin.php?action=revoke_token&id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: adminHeaders(),
    });

    if (response.ok) {
        Toast.show('Token revoked', 'success');
        void loadTokens();
    }
}

// Styled confirm modal
function showConfirmModal(message: string, title = 'Confirm'): Promise<boolean> {
    return new Promise(resolve => {
        const overlay = document.createElement('div');
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10000;
            backdrop-filter: blur(2px);
        `;

        const modal = document.createElement('div');
        modal.style.cssText = `
            background: white;
            border-radius: 8px;
            padding: 30px;
            max-width: 450px;
            box-shadow: 0 10px 40px rgba(0,0,0,0.3);
            animation: modalSlideIn 0.2s ease-out;
        `;

        const styleEl = document.createElement('style');
        styleEl.textContent = `
            @keyframes modalSlideIn {
                from { opacity: 0; transform: scale(0.9) translateY(-20px); }
                to { opacity: 1; transform: scale(1) translateY(0); }
            }
        `;
        document.head.appendChild(styleEl);

        modal.innerHTML = `
            <h3 style="margin: 0 0 15px 0; color: #2c3e50; font-size: 20px;">${escapeHtml(title)}</h3>
            <p style="margin: 0 0 25px 0; color: #555; font-size: 15px; line-height: 1.5;">${escapeHtml(message)}</p>
            <div style="display: flex; gap: 10px; justify-content: flex-end;">
                <button id="cancelBtn" style="background: #95a5a6; color: white; border: none; padding: 10px 20px; border-radius: 5px; font-size: 14px; font-weight: 600; cursor: pointer;">Cancel</button>
                <button id="confirmBtn" style="background: #e74c3c; color: white; border: none; padding: 10px 20px; border-radius: 5px; font-size: 14px; font-weight: 600; cursor: pointer;">Confirm</button>
            </div>
        `;

        overlay.appendChild(modal);
        document.body.appendChild(overlay);

        const cleanup = () => {
            document.body.removeChild(overlay);
            document.head.removeChild(styleEl);
        };

        modal.querySelector<HTMLButtonElement>('#cancelBtn')!.onclick = () => {
            cleanup();
            resolve(false);
        };
        modal.querySelector<HTMLButtonElement>('#confirmBtn')!.onclick = () => {
            cleanup();
            resolve(true);
        };
        overlay.onclick = e => {
            if (e.target === overlay) {
                cleanup();
                resolve(false);
            }
        };
    });
}

function copyToken(): void {
    const token = el('newTokenDisplay').textContent || '';
    void navigator.clipboard.writeText(token);
    Toast.show('Token copied to clipboard!', 'success');
}

function closeModal(): void {
    el('tokenModal').style.display = 'none';
}

// ===== INVITE CODE FUNCTIONS =====

async function loadInvites(): Promise<void> {
    const response = await fetch(`${API_BASE}/php/auth_invite.php?action=list`, { headers: adminHeaders() });
    const data = await response.json();

    const tbody = el('invitesTableBody');
    tbody.innerHTML = '';

    (data.invites as InviteRecord[]).forEach(invite => {
        const tr = document.createElement('tr');

        const maxUses = invite.max_uses != null ? parseInt(String(invite.max_uses)) : 1;
        let useCount = parseInt(String(invite.use_count)) || 0;
        // Backward compat: old single-use invites have used_at but no invite_uses records
        if (useCount === 0 && invite.used_at && maxUses === 1) useCount = 1;
        const isFullyUsed = maxUses > 0 && useCount >= maxUses;
        const isExpired = !!invite.expires_at && new Date(invite.expires_at) < new Date();

        let statusText = 'Available';
        let statusClass = 'status-active';

        if (isFullyUsed) {
            statusText = 'Fully Used';
            statusClass = 'status-revoked';
        } else if (isExpired) {
            statusText = 'Expired';
            statusClass = 'status-warning';
        } else if (useCount > 0) {
            statusText = 'Active';
            statusClass = 'status-active';
        }

        // Format expiry
        let expiryText = 'Never';
        if (invite.expires_at) {
            expiryText = new Date(invite.expires_at).toLocaleDateString();
        }

        // Format uses
        const usesText = maxUses === 0 ? `${useCount} / ∞` : `${useCount} / ${maxUses}`;

        const copyButton = !isFullyUsed
            ? `<button class="btn btn-success" data-action="copy-invite-url" data-code="${escapeHtml(invite.code)}" data-target-dir="${escapeHtml(invite.target_dir || '')}" style="padding: 6px 12px; font-size: 12px;">🔗 Copy URL</button>`
            : '-';
        const deleteButton = !isFullyUsed && !isExpired
            ? `<button class="btn btn-danger" data-action="delete-invite" data-id="${invite.id}" style="padding: 6px 12px; font-size: 12px;">Delete</button>`
            : '';

        tr.innerHTML = `
            <td><span class="token-preview">${escapeHtml(invite.code)}</span></td>
            <td>${escapeHtml(invite.notes || '-')}</td>
            <td>${new Date(invite.created_at).toLocaleDateString()}</td>
            <td>${expiryText}</td>
            <td>${usesText}</td>
            <td class="${statusClass}">${statusText}</td>
            <td>
                ${copyButton}
                ${deleteButton}
            </td>
        `;
        tbody.appendChild(tr);
    });
}

async function createInvite(): Promise<void> {
    const notes = el<HTMLInputElement>('newInviteNotes').value;
    const expiry = el<HTMLInputElement>('newInviteExpiry').value;
    const maxUses = el<HTMLInputElement>('newInviteMaxUses').value;
    let targetDir = el<HTMLInputElement>('newInviteTargetDir').value.trim();

    // If a full URL was pasted, extract just the hash portion
    if (targetDir && (targetDir.startsWith('http://') || targetDir.startsWith('https://'))) {
        try {
            const parsed = new URL(targetDir);
            targetDir = parsed.hash.substring(1) || ''; // Remove leading #
        } catch { /* leave as-is */ }
    }

    const response = await fetch(`${API_BASE}/php/auth_invite.php?action=create`, {
        method: 'POST',
        headers: adminHeaders(true),
        body: JSON.stringify({
            notes: notes,
            expires_in_days: expiry ? parseInt(expiry) : null,
            max_uses: parseInt(maxUses),
            target_dir: targetDir || null,
        }),
    });

    const data = await response.json();

    if (data.success) {
        el('newInviteCodeDisplay').textContent = data.code;
        el('newInviteUrlDisplay').textContent = inviteUrl(data.code, targetDir);
        el('inviteModal').style.display = 'block';
        el<HTMLInputElement>('newInviteNotes').value = '';
        el<HTMLInputElement>('newInviteTargetDir').value = '';
        void loadInvites();
    } else {
        Toast.show('Failed to create invite code', 'error');
    }
}

function inviteUrl(code: string, targetDir: string): string {
    let url = `${window.location.origin}/?invite=${code}`;
    if (targetDir) {
        url += `&dir=${encodeURIComponent(targetDir)}`;
    }
    return url;
}

async function deleteInvite(id: string): Promise<void> {
    const confirmed = await showConfirmModal('This action cannot be undone.', 'Delete Invite Code?');
    if (!confirmed) return;

    const response = await fetch(`${API_BASE}/php/auth_invite.php?action=delete&id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: adminHeaders(),
    });

    if (response.ok) {
        Toast.show('Invite code deleted', 'success');
        void loadInvites();
    }
}

function copyInviteCode(): void {
    const code = el('newInviteCodeDisplay').textContent || '';
    void navigator.clipboard.writeText(code);
    Toast.show('Invite code copied to clipboard!', 'success');
}

function copyInviteUrl(): void {
    const url = el('newInviteUrlDisplay').textContent || '';
    void navigator.clipboard.writeText(url);
    Toast.show('Invite URL copied to clipboard!', 'success');
}

function copyInviteUrlFromTable(code: string, targetDir: string): void {
    void navigator.clipboard.writeText(inviteUrl(code, targetDir));
    Toast.show('Invite URL copied!', 'success');
}

function closeInviteModal(): void {
    el('inviteModal').style.display = 'none';
}

// ===== PLUGIN MARKETPLACE FUNCTIONS =====

let registryPlugins: RegistryPlugin[] = [];

async function loadPluginSettings(): Promise<void> {
    try {
        const response = await fetch(`${API_BASE}/php/plugin_admin.php?action=settings`, { headers: adminHeaders() });
        const data = await response.json();
        if (data.success && data.settings) {
            el<HTMLInputElement>('registryUrlInput').value = data.settings.registry_url || '';
        }
    } catch (error) {
        console.error('Failed to load plugin settings:', error);
    }
}

async function updateRegistryUrl(): Promise<void> {
    const url = el<HTMLInputElement>('registryUrlInput').value.trim();
    if (!url) {
        Toast.show('Please enter a registry URL', 'warning');
        return;
    }

    try {
        const response = await fetch(`${API_BASE}/php/plugin_admin.php?action=settings`, {
            method: 'PUT',
            headers: adminHeaders(true),
            body: JSON.stringify({ registry_url: url }),
        });

        const data = await response.json();
        if (data.success) {
            Toast.show('Registry URL updated', 'success');
        } else {
            Toast.show(data.error || 'Failed to update URL', 'error');
        }
    } catch (error) {
        Toast.show('Error: ' + (error as Error).message, 'error');
    }
}

/** Status badge and action buttons for one registry row. */
function pluginRowParts(plugin: RegistryPlugin): { statusHtml: string; actionsHtml: string; versionText: string } {
    const id = escapeHtml(plugin.id);
    const name = escapeHtml(plugin.name);
    const buttonStyle = 'style="padding: 6px 12px; font-size: 12px;"';
    let statusHtml = '';
    let actionsHtml = '';

    if (!plugin.installed) {
        statusHtml = '<span class="plugin-status status-not-installed">Not Installed</span>';
        actionsHtml = `<button class="btn btn-success" data-action="install-plugin" data-id="${id}" ${buttonStyle}>Install</button>`;
    } else if (plugin.manuallyInstalled) {
        // Manually installed (on disk but not in database)
        statusHtml = '<span class="plugin-status status-manual">Manual</span>';
        actionsHtml = `<button class="btn btn-danger" data-action="uninstall-plugin" data-id="${id}" data-name="${name}" ${buttonStyle}>Uninstall</button>`;
    } else {
        // Installed - check for updates and enabled state
        const hasUpdate = plugin.installedVersion !== plugin.version;
        const isEnabled = plugin.enabled !== false;
        const toggleAction = isEnabled ? 'disable' : 'enable';
        const toggleText = isEnabled ? 'Disable' : 'Enable';
        const toggleClass = isEnabled ? 'btn-warning' : 'btn-success';

        if (hasUpdate) {
            statusHtml = `<span class="plugin-status status-update">${escapeHtml(plugin.installedVersion)} → ${escapeHtml(plugin.version)}</span>`;
        } else if (isEnabled) {
            statusHtml = '<span class="plugin-status status-active">Enabled</span>';
        } else {
            statusHtml = '<span class="plugin-status status-disabled">Disabled</span>';
        }

        actionsHtml = `
            ${hasUpdate ? `<button class="btn" data-action="update-plugin" data-id="${id}" ${buttonStyle}>Update</button>` : ''}
            <button class="btn ${toggleClass}" data-action="toggle-plugin" data-id="${id}" data-toggle="${toggleAction}" ${buttonStyle}>${toggleText}</button>
            <button class="btn btn-danger" data-action="uninstall-plugin" data-id="${id}" data-name="${name}" ${buttonStyle}>Uninstall</button>
        `;
    }

    const versionText = plugin.manuallyInstalled ? '-' : (plugin.installed ? plugin.installedVersion : plugin.version);
    return { statusHtml, actionsHtml, versionText: versionText || '-' };
}

async function refreshRegistry(): Promise<void> {
    const loadingEl = el('pluginsLoading');
    const tableEl = el('pluginsTable');

    loadingEl.textContent = 'Loading plugins...';
    loadingEl.classList.remove('hidden');
    tableEl.classList.add('hidden');

    try {
        const response = await fetch(`${API_BASE}/php/plugin_admin.php?action=registry`, { headers: adminHeaders() });
        const data = await response.json();

        if (!data.success) {
            loadingEl.textContent = data.error || 'Failed to load registry';
            return;
        }

        registryPlugins = data.plugins || [];

        if (registryPlugins.length === 0) {
            loadingEl.textContent = 'No plugins found in registry';
            return;
        }

        const tbody = el('pluginsTableBody');
        tbody.innerHTML = '';

        registryPlugins.forEach(plugin => {
            const tr = document.createElement('tr');
            tr.setAttribute('data-plugin-id', plugin.id);
            const { statusHtml, actionsHtml, versionText } = pluginRowParts(plugin);
            tr.innerHTML = `
                <td class="plugin-icon">${escapeHtml(plugin.icon || '📦')}</td>
                <td><strong>${escapeHtml(plugin.name)}</strong></td>
                <td class="plugin-description">${escapeHtml(plugin.description || '-')}</td>
                <td class="plugin-version">${escapeHtml(versionText)}</td>
                <td class="plugin-status-cell">${statusHtml}</td>
                <td class="plugin-actions">${actionsHtml}</td>
            `;
            tbody.appendChild(tr);
        });

        loadingEl.classList.add('hidden');
        tableEl.classList.remove('hidden');
        updateUpdateAllButton();
    } catch (error) {
        loadingEl.textContent = 'Error: ' + (error as Error).message;
    }
}

function pluginRow(pluginId: string): HTMLTableRowElement | null {
    return document.querySelector<HTMLTableRowElement>(`tr[data-plugin-id="${CSS.escape(pluginId)}"]`);
}

function setRowLoading(pluginId: string, loading: boolean): void {
    const row = pluginRow(pluginId);
    if (!row) return;
    const actionsCell = row.querySelector('.plugin-actions');
    if (loading) {
        row.style.opacity = '0.6';
        row.style.pointerEvents = 'none';
        if (actionsCell) actionsCell.innerHTML = '<span class="loading-spinner-small"></span>';
    } else {
        row.style.opacity = '1';
        row.style.pointerEvents = 'auto';
    }
}

function updatePluginRow(plugin: RegistryPlugin): void {
    const row = pluginRow(plugin.id);
    if (!row) return;

    const { statusHtml, actionsHtml, versionText } = pluginRowParts(plugin);
    row.querySelector('.plugin-status-cell')!.innerHTML = statusHtml;
    row.querySelector('.plugin-actions')!.innerHTML = actionsHtml;
    row.querySelector('.plugin-version')!.textContent = versionText;
    row.style.opacity = '1';
    row.style.pointerEvents = 'auto';

    // Update local cache
    const idx = registryPlugins.findIndex(p => p.id === plugin.id);
    if (idx >= 0) registryPlugins[idx] = plugin;

    updateUpdateAllButton();
}

async function pluginAction(action: string, pluginId: string): Promise<{ success: boolean; error?: string; plugin?: { name: string; version: string } }> {
    const response = await fetch(`${API_BASE}/php/plugin_admin.php?action=${action}`, {
        method: 'POST',
        headers: adminHeaders(true),
        body: JSON.stringify({ plugin_id: pluginId }),
    });
    return response.json();
}

async function installPlugin(pluginId: string): Promise<void> {
    setRowLoading(pluginId, true);
    try {
        const data = await pluginAction('install', pluginId);
        if (data.success && data.plugin) {
            Toast.show(`${data.plugin.name} installed successfully!`, 'success');
            const plugin = registryPlugins.find(p => p.id === pluginId);
            if (plugin) {
                plugin.installed = true;
                plugin.installedVersion = data.plugin.version;
                plugin.enabled = true;
                updatePluginRow(plugin);
            }
        } else {
            Toast.show(data.error || 'Installation failed', 'error');
            setRowLoading(pluginId, false);
        }
    } catch (error) {
        Toast.show('Error: ' + (error as Error).message, 'error');
        setRowLoading(pluginId, false);
    }
}

async function updatePlugin(pluginId: string): Promise<void> {
    setRowLoading(pluginId, true);
    try {
        const data = await pluginAction('update', pluginId);
        if (data.success && data.plugin) {
            Toast.show(`${data.plugin.name} updated to ${data.plugin.version}!`, 'success');
            const plugin = registryPlugins.find(p => p.id === pluginId);
            if (plugin) {
                plugin.installedVersion = data.plugin.version;
                updatePluginRow(plugin);
            }
        } else {
            Toast.show(data.error || 'Update failed', 'error');
            setRowLoading(pluginId, false);
        }
    } catch (error) {
        Toast.show('Error: ' + (error as Error).message, 'error');
        setRowLoading(pluginId, false);
    }
}

function pluginsWithUpdates(): RegistryPlugin[] {
    return registryPlugins.filter(p => p.installed && p.installedVersion && p.installedVersion !== p.version);
}

async function updateAllPlugins(): Promise<void> {
    const pending = pluginsWithUpdates();
    if (pending.length === 0) {
        Toast.show('No updates available', 'info');
        return;
    }

    const updateAllBtn = el<HTMLButtonElement>('updateAllBtn');
    const originalText = updateAllBtn.textContent;
    updateAllBtn.disabled = true;

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < pending.length; i++) {
        const plugin = pending[i];
        updateAllBtn.textContent = `Updating ${i + 1}/${pending.length}...`;
        try {
            const data = await pluginAction('update', plugin.id);
            if (data.success && data.plugin) {
                plugin.installedVersion = data.plugin.version;
                updatePluginRow(plugin);
                successCount++;
            } else {
                failCount++;
            }
        } catch {
            failCount++;
        }
    }

    updateAllBtn.disabled = false;
    updateAllBtn.textContent = originalText;
    updateUpdateAllButton();

    if (failCount === 0) {
        Toast.show(`Updated ${successCount} plugin${successCount !== 1 ? 's' : ''} successfully!`, 'success');
    } else {
        Toast.show(`Updated ${successCount}, failed ${failCount}`, failCount > successCount ? 'error' : 'warning');
    }
}

function updateUpdateAllButton(): void {
    const pending = pluginsWithUpdates();
    const btn = el('updateAllBtn');
    if (pending.length > 0) {
        btn.style.display = 'inline-block';
        btn.textContent = `⬆️ Update All (${pending.length})`;
    } else {
        btn.style.display = 'none';
    }
}

async function togglePlugin(pluginId: string, action: string): Promise<void> {
    setRowLoading(pluginId, true);
    try {
        const data = await pluginAction(action, pluginId);
        if (data.success) {
            Toast.show(`Plugin ${action}d`, 'success');
            const plugin = registryPlugins.find(p => p.id === pluginId);
            if (plugin) {
                plugin.enabled = action === 'enable';
                updatePluginRow(plugin);
            }
        } else {
            Toast.show(data.error || `Failed to ${action} plugin`, 'error');
            setRowLoading(pluginId, false);
        }
    } catch (error) {
        Toast.show('Error: ' + (error as Error).message, 'error');
        setRowLoading(pluginId, false);
    }
}

async function uninstallPlugin(pluginId: string, pluginName: string): Promise<void> {
    const confirmed = await showConfirmModal(
        `This will remove "${pluginName}" and all its files. Documents created with this plugin may become inaccessible.`,
        'Uninstall Plugin?'
    );
    if (!confirmed) return;

    setRowLoading(pluginId, true);
    try {
        const data = await pluginAction('uninstall', pluginId);
        if (data.success) {
            Toast.show('Plugin uninstalled', 'success');
            const plugin = registryPlugins.find(p => p.id === pluginId);
            if (plugin) {
                plugin.installed = false;
                plugin.installedVersion = null;
                plugin.enabled = undefined;
                updatePluginRow(plugin);
            }
        } else {
            Toast.show(data.error || 'Uninstallation failed', 'error');
            setRowLoading(pluginId, false);
        }
    } catch (error) {
        Toast.show('Error: ' + (error as Error).message, 'error');
        setRowLoading(pluginId, false);
    }
}

// ===== EVENT WIRING =====

/** Every data-action in the page, static or rendered; the dataset carries the arguments. */
const actions: Record<string, (data: DOMStringMap) => void | Promise<void>> = {
    'login': () => login(),
    'logout': () => logout(),
    'refresh': () => loadData(),
    'switch-tab': data => switchTab(data.tab || 'access'),
    'create-token': () => createToken(),
    'revoke-token': data => revokeToken(data.id || ''),
    'copy-token': () => copyToken(),
    'close-token-modal': () => closeModal(),
    'create-invite': () => createInvite(),
    'delete-invite': data => deleteInvite(data.id || ''),
    'copy-invite-code': () => copyInviteCode(),
    'copy-invite-url-modal': () => copyInviteUrl(),
    'copy-invite-url': data => copyInviteUrlFromTable(data.code || '', data.targetDir || ''),
    'close-invite-modal': () => closeInviteModal(),
    'refresh-registry': () => refreshRegistry(),
    'update-all-plugins': () => updateAllPlugins(),
    'update-registry-url': () => updateRegistryUrl(),
    'install-plugin': data => installPlugin(data.id || ''),
    'update-plugin': data => updatePlugin(data.id || ''),
    'toggle-plugin': data => togglePlugin(data.id || '', data.toggle || 'enable'),
    'uninstall-plugin': data => uninstallPlugin(data.id || '', data.name || ''),
};

document.addEventListener('click', event => {
    const trigger = (event.target as Element | null)?.closest<HTMLElement>('[data-action]');
    if (!trigger) return;
    const action = actions[trigger.dataset.action || ''];
    if (!action) return;
    event.preventDefault();
    void action(trigger.dataset);
});

el<HTMLInputElement>('authRequiredToggle').addEventListener('change', () => void toggleAuthRequired());

// Restore tab preference, then try the saved token
const savedTab = localStorage.getItem('adminTab');
if (savedTab) switchTab(savedTab);
void autoLogin();
